import { useEffect, useRef, useCallback } from 'react';

/**
 * Premium scroll reveal hook using IntersectionObserver.
 * Adds 'visible' class to element and all children with .sr/.sr-scale/.sr-left/.sr-right
 */
export function useReveal(opts?: { threshold?: number; rootMargin?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          el.querySelectorAll('.sr, .sr-scale, .sr-left, .sr-right').forEach((c) =>
            c.classList.add('visible')
          );
          io.unobserve(el);
        }
      },
      { threshold: opts?.threshold ?? 0.12, rootMargin: opts?.rootMargin ?? '0px 0px -80px 0px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return ref;
}

/**
 * Staggered reveal: children get 'visible' class with setTimeout delays.
 */
export function useStagger(delayMs = 100, opts?: { threshold?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const children = el.querySelectorAll('.sr, .sr-scale, .sr-left, .sr-right');
          children.forEach((child, i) => {
            setTimeout(() => child.classList.add('visible'), i * delayMs);
          });
          io.unobserve(el);
        }
      },
      { threshold: opts?.threshold ?? 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return ref;
}
